import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { MapPicker, SpeakingOrder } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";

const TASK_ID = 4;
const groupId = 0;

const Task4 = ({ summary = false, pdf = false }) => {
  const [taskOrderUsers, setTaskOrderUsers] = useState([]);
  const [isStarted, setIsStarted] = useState(false);
  const { t } = useTranslation();

  const {
    socket,
    user: { id, name, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  useEffect(() => {
    socket.emit("taskOrder", TASK_ID, (users) => {
      if (users) setTaskOrderUsers(users);
    });

    socket.emit("groupTaskStatus", TASK_ID, (status) => {
      if (status?.[groupId]?.started) {
        setIsStarted(true);
      }
    });

    socket.on("groupTaskStatus", (status) => {
      if (status?.[TASK_ID]?.[groupId]?.started) {
        setIsStarted(true);
      }
    });
  }, []);

  const handleNext = () => {
    if (!isStarted) {
      updateGroupTaskStatus({
        taskId: TASK_ID,
        groupId,
        started: true
      });
      setIsStarted(true);
      return;
    }
    const nextRoute = getNextRoute("/task-4", platform);
    socket.emit("navigate", nextRoute);
  };

  const updateGroupTaskStatus = (data) => {
    socket.emit("updateGroupTaskStatus", data);
  };

  const buttonText =
    !isStarted && role !== defaultRoles.participant
      ? t("task4.buttonStart") + " →"
      : t("task4.buttonNext") + " →";

  const handleButtonClick = () => {
    if (role !== defaultRoles.participant) {
      return handleNext;
    } else return null;
  };

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-4`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted ? t("task4.started.title") : t("task4.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html: isStarted ? t("task4.started.description") : t("task4.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <>
          <div className="flex gap-4 flex-col-reverse md:flex-row justify-center items-center">
            <div className="max-w-[90vw] w-[500px] md:w-9/12 h-full">
              <div className="flex justify-center mb-6">
                <SpeakingOrder participants={taskOrderUsers} filterOnlineParticipants={true} />
              </div>
              <MapPicker
                socket={socket}
                id={"task4"}
                updateId={"updateTask4"}
                name={name}
                userId={id}
                moderator={role === defaultRoles.moderator}
                summary={summary}
                pdf={pdf}
              />
            </div>
          </div>
          <div className="overflow-y-auto mt-10 w-full">
            <Conference isLinear={true} />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 w-full">
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl col-span-1 h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4 text-left">
              {t("task4.card.title")}
            </h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task4.card.list.item1") }}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            {/* Participants Grid */}
            <Conference smallSpace={true} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task4;
